import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader } from 'rsuite';

import { useMessenger } from '../../context/messengerContext';
import { getNotification } from '../../api/notifications';
import { formatDateTime } from '../../utils/format';
import { downloadFile } from '../../utils/downloadFile';
import { getNotificationTitle, getNotificationText } from '../../utils/notificationTypeMap';

import './notificationDetail.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

export default function NotificationDetail() {
    const { t } = useTranslation('basic');
    const { id } = useParams();
    const { userId, platform } = useMessenger();

    const [notification, setNotification] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isIOS26Version = isIOSVersionHigherThan26Version();

    const loadNotification = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getNotification({
                platform,
                userId,
                notification_id: id,
            });
            setNotification(data);
        } catch (err) {
            setError(err?.message || t('notifications_load_error'));
        } finally {
            setLoading(false);
        }
    }, [id, platform, userId, t]);

    useEffect(() => {
        loadNotification();
    }, [loadNotification]);

    if (loading) {
        return (
            <div className={`notificationDetail ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
                <div className='notificationDetail_loader'>
                    <Loader size='lg' />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`notificationDetail ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
                <p className='notificationDetail_error'>{error}</p>
            </div>
        );
    }

    if (!notification) {
        return null;
    }

    const hasFiles =
        Array.isArray(notification.files) && notification.files.length > 0;

    return (
        <div className={`notificationDetail ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
            <div className='notificationDetail_card'>
                <div className='notificationDetail_header'>
                    <h1 className='notificationDetail_title heading-h4'>
                        {getNotificationTitle(notification.type, notification.title, t)}
                    </h1>
                    <div className='notificationDetail_date'>
                        {formatDateTime(notification.created_at)}
                    </div>
                </div>
                <div className='notificationDetail_body'>
                    <div className='notificationDetail_text'>
                        <div dangerouslySetInnerHTML={{ __html: getNotificationText(notification.type, notification.text, t) }}></div>
                    </div>
                </div>
                {hasFiles && (
                    <div className='notificationDetail_files'>
                        <div className='notificationDetail_filesTitle'>
                            {t('notifications_files')}
                        </div>
                        <ul className='notificationDetail_filesList'>
                            {notification.files.map((file, index) => (
                                <li
                                    key={index}
                                    className='notificationDetail_fileItem'
                                >
                                    <button
                                        type='button'
                                        className='notificationDetail_fileLink'
                                        onClick={() =>
                                            downloadFile(
                                                file.url,
                                                file.name,
                                                notification.contract_number,
                                            )
                                        }
                                    >
                                        {file.name ||
                                            file.url ||
                                            t('notifications_file_download')}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
